import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "panel",
    "toggleButton",
    "activeFilters",
    "filterTag",
    "clearButton",
  ];

  connect() {
    console.log("Filter panel controller connected");
    this.updateActiveFilters();
  }

  // Helper function to create Phosphor icon SVG
  createIcon(iconName, className = "w-3.5 h-3.5") {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("class", className);
    svg.setAttribute("fill", "currentColor");
    svg.setAttribute("viewBox", "0 0 256 256");

    // Icon paths for commonly used icons (Regular weight)
    const iconPaths = {
      funnel:
        "M227.81,66.76l-.08.09L160,139.17V216a8,8,0,0,1-3.56,6.66l-32,21.33A8,8,0,0,1,112,237.33V139.17L44.27,66.85l-.08-.09A16,16,0,0,1,56,40H200a16,16,0,0,1,11.84,26.76ZM200,56H56l64,72.61V229.33l16-10.66V128.61Z",
      folder:
        "M216,72H130.67L102.93,51.2a16.14,16.14,0,0,0-9.6-3.2H40A16,16,0,0,0,24,64V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V88A16,16,0,0,0,216,72Zm0,128H40V64H93.33l27.74,20.8a16.14,16.14,0,0,0,9.6,3.2H216Z",
      "currency-dollar":
        "M152,120H136V56h8a32,32,0,0,1,32,32,8,8,0,0,0,16,0,48.05,48.05,0,0,0-48-48h-8V24a8,8,0,0,0-16,0V40h-8a48,48,0,0,0,0,96h8v64H104a32,32,0,0,1-32-32,8,8,0,0,0-16,0,48.05,48.05,0,0,0,48,48h16v16a8,8,0,0,0,16,0V216h16a48,48,0,0,0,0-96Zm-40,0a32,32,0,0,1,0-64h8v64Zm40,80H136V136h16a32,32,0,0,1,0,64Z",
      calendar:
        "M208,32H184V24a8,8,0,0,0-16,0v8H88V24a8,8,0,0,0-16,0v8H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32ZM72,48v8a8,8,0,0,0,16,0V48h80v8a8,8,0,0,0,16,0V48h24V80H48V48ZM208,208H48V96H208V208Z",
      "check-circle":
        "M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z",
      tag: "M243.31,136,144,36.69A15.86,15.86,0,0,0,132.69,32H40a8,8,0,0,0-8,8v92.69A15.86,15.86,0,0,0,36.69,144L136,243.31a16,16,0,0,0,22.63,0l84.68-84.68a16,16,0,0,0,0-22.63Zm-96,96L48,132.69V48h84.69L232,147.31ZM96,84A12,12,0,1,1,84,72,12,12,0,0,1,96,84Z",
      "map-pin":
        "M128,64a40,40,0,1,0,40,40A40,40,0,0,0,128,64Zm0,64a24,24,0,1,1,24-24A24,24,0,0,1,128,128Zm0-112a88.1,88.1,0,0,0-88,88c0,31.4,14.51,64.68,42,96.25a254.19,254.19,0,0,0,41.45,38.3,8,8,0,0,0,9.18,0A254.19,254.19,0,0,0,174,200.25c27.45-31.57,42-64.85,42-96.25A88.1,88.1,0,0,0,128,16Zm0,206c-16.53-13-72-60.75-72-118a72,72,0,0,1,144,0C200,161.23,144.53,209,128,222Z",
      x: "M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z",
    };

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", iconPaths[iconName] || iconPaths["tag"]);
    svg.appendChild(path);

    return svg;
  }

  toggle() {
    this.panelTarget.classList.toggle("hidden");

    // Update button text only - keep the icon as-is
    const isHidden = this.panelTarget.classList.contains("hidden");
    const span = this.toggleButtonTarget.querySelector("span");
    if (span) {
      span.textContent = isHidden ? "Filter" : "Hide Filters";
    }
  }

  clearFilters(event) {
    event.preventDefault();

    // Reset all form inputs
    const form = this.element.closest("form");
    if (!form) {
      console.error("Form not found");
      return;
    }

    const inputs = form.querySelectorAll(
      "input[type='checkbox'], input[type='radio']"
    );

    inputs.forEach((input) => {
      if (input.type === "checkbox") {
        input.checked = false;
      } else if (input.type === "radio" && input.value === "") {
        input.checked = true;
      } else if (input.type === "radio") {
        input.checked = false;
      }
    });

    // Reset the search input
    const searchInput = form.querySelector("input[type='text']");
    if (searchInput) searchInput.value = "";

    // Submit the form automatically to update results
    form.requestSubmit();

    // Clear active filters display
    this.updateActiveFilters();
  }

  removeFilter(event) {
    event.preventDefault();
    const filterType = event.currentTarget.dataset.filterType;
    const filterValue = event.currentTarget.dataset.filterValue;

    // Find the form
    const form = this.element.closest("form");
    if (!form) {
      console.error("Form not found");
      return;
    }

    // Handle search filter specially
    if (filterType === "search") {
      const searchInput = form.querySelector(`input[name='search']`);
      if (searchInput) {
        searchInput.value = "";
      }
    } else {
      // Find and uncheck the corresponding checkbox/radio button
      const input = form.querySelector(
        `input[name*='[${filterType}]'][value='${filterValue}']`
      );
      if (input) {
        input.checked = false;
      }
    }

    // Submit the form automatically
    form.requestSubmit();

    // Update the UI
    this.updateActiveFilters();
  }

  updateActiveFilters() {
    console.log("Updating active filters");
    const activeFilters = [];
    const form = this.element.closest("form");
    if (!form) {
      console.error("Form not found for active filters");
      return;
    }

    // Get all checked inputs
    const checkedInputs = form.querySelectorAll(
      "input[type='checkbox']:checked, input[type='radio']:checked"
    );

    checkedInputs.forEach((input) => {
      if (input.value && input.value !== "") {
        let filterName = "";
        const nameMatch = input.name.match(/\[(.*?)\]/);
        if (nameMatch && nameMatch[1]) {
          filterName = nameMatch[1];
        } else {
          return; // Skip if we can't determine the filter name
        }

        const label = input.nextElementSibling;
        let filterLabel = label ? label.textContent.trim() : input.value;

        // Format specific filter types for better display
        if (filterName === "budget_range" && filterLabel.includes("$")) {
          // For budget range, display the actual price range
          filterLabel = filterLabel.replace("USD", "").trim();
        }

        activeFilters.push({
          type: filterName,
          value: input.value,
          label: filterLabel,
        });
      }
    });

    // Get search input if it has a value
    const searchInput = form.querySelector("input[name='search']");
    if (searchInput && searchInput.value.trim()) {
      activeFilters.push({
        type: "search",
        value: searchInput.value.trim(),
        label: `"${searchInput.value.trim()}"`,
      });
    }

    console.log("Active filters:", activeFilters);

    // Update the active filters display
    this.renderActiveFilters(activeFilters);
  }

  renderActiveFilters(filters) {
    console.log("Rendering active filters", filters);
    // Create the active filters display
    const activeFiltersContainer = this.activeFiltersTarget;

    // Clear existing content
    activeFiltersContainer.innerHTML = "";

    if (filters.length > 0) {
      // Create filter tags with new design
      filters.forEach((filter) => {
        const tag = document.createElement("div");
        tag.className =
          "flex items-center h-6 gap-1 px-2.5 py-0.5 bg-stone-100 border border-stone-100 rounded-lg";
        tag.dataset.filterType = filter.type;
        tag.dataset.filterValue = filter.value;

        // Get icon name based on filter type
        let iconName = "tag"; // default icon
        if (filter.type === "platform") {
          iconName = "map-pin";
        } else if (filter.type === "job_category") {
          iconName = "folder";
        } else if (filter.type === "budget_range") {
          iconName = "currency-dollar";
        } else if (filter.type === "payment_frequency") {
          iconName = "calendar";
        } else if (filter.type === "status") {
          iconName = "check-circle";
        } else if (filter.type === "topics") {
          iconName = "tag";
        }

        // Create icon
        const icon = this.createIcon(iconName, "w-3 h-3 text-stone-700");

        // Create label
        const label = document.createElement("span");
        label.className = "text-[10.7px] font-normal leading-4 text-stone-700";
        label.textContent = filter.label;

        // Create remove button
        const removeButton = document.createElement("button");
        removeButton.type = "button";
        removeButton.className = "ml-1 text-stone-400 hover:text-stone-700";
        removeButton.dataset.action = "filter-panel#removeFilter";
        removeButton.dataset.filterType = filter.type;
        removeButton.dataset.filterValue = filter.value;

        const closeIcon = this.createIcon("x", "w-3 h-3");
        removeButton.appendChild(closeIcon);

        // Assemble the tag
        tag.appendChild(icon);
        tag.appendChild(label);
        tag.appendChild(removeButton);

        activeFiltersContainer.appendChild(tag);
      });

      // Show the clear button if we have filters
      if (this.hasClearButtonTarget) {
        this.clearButtonTarget.classList.remove("hidden");
      }
    } else {
      // Hide the clear button if no filters
      if (this.hasClearButtonTarget) {
        this.clearButtonTarget.classList.add("hidden");
      }
    }
  }

  submitForm() {
    this.updateActiveFilters();

    // Close the filter panel after form submission
    if (this.hasPanelTarget) {
      this.panelTarget.classList.add("hidden");

      // Update the toggle button text
      if (this.hasToggleButtonTarget) {
        const icon = this.createIcon("funnel");
        const span = document.createElement("span");
        span.className = "leading-5";
        span.textContent = "Filter";

        this.toggleButtonTarget.innerHTML = "";
        this.toggleButtonTarget.appendChild(icon);
        this.toggleButtonTarget.appendChild(span);
      }
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
}
